import React from 'react';
import { t } from '../utils/lang.js';
import { setUiLang, useUiLang } from '../utils/lang.js';
import { settingsAPI } from '../utils/api.js';
import CustomSelect from './CustomSelect.jsx';
import { ensureQcfFont, ensureIndopakFont } from '../qcfFontLoader.js';
import { prefetchSurah } from '../surahData.js';
import TranslationSelector from './TranslationSelector.jsx';
import { getTranslationById, getDefaultTranslations } from '../utils/translations.js';
import { useAudio, RECITERS } from '../audio/AudioProvider.jsx';

const themes = [
  { id: 'light', nameKey: 'light' },
  { id: 'dark', nameKey: 'dark' }
];

const uiLanguages = [
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'العربية' }
];

const recitersList = Object.entries(RECITERS).map(([id, m]) => ({ id, name: m.name }));

export default function SettingsModal({ isOpen, onClose }) {
  const audio = useAudio();
  const [theme, setTheme] = React.useState(() => localStorage.getItem('theme') || (document.documentElement.getAttribute('data-theme') || 'dark'));
  const uiLang = useUiLang();
  const [qcfStyle, setQcfStyle] = React.useState(() => localStorage.getItem('qcfStyle') || 'v1');
  const [fontSize, setFontSize] = React.useState(() => {
    const saved = localStorage.getItem('fontSize');
    const parsed = saved ? parseInt(saved, 10) : 3;
    return Number.isFinite(parsed) ? parsed : 3;
  });
  const [translationFontSize, setTranslationFontSize] = React.useState(() => {
    const saved = localStorage.getItem('translationFontSize');
    const parsed = saved ? parseInt(saved, 10) : 3;
    return Number.isFinite(parsed) ? parsed : 3;
  });
  const [reciter, setReciter] = React.useState(() => localStorage.getItem('selectedReciter') || audio?.reciter || 'Mishari_Rashid_al-Afasy');
  const [wordByWord, setWordByWord] = React.useState(() => {
    const saved = localStorage.getItem('wordByWord');
    return saved ? JSON.parse(saved) : { translation: true, transliteration: false, reading: true };
  });
  const [wordByWordDisplay, setWordByWordDisplay] = React.useState(() => localStorage.getItem('wordByWordDisplay') || 'inline');
  const [wordByWordLang, setWordByWordLang] = React.useState(() => localStorage.getItem('wordByWordLang') || 'ru');
  const [previewData, setPreviewData] = React.useState(null);

  const [showTranslation, setShowTranslation] = React.useState(() => {
    try {
      const savedIdsRaw = localStorage.getItem('selectedTranslations');
      const savedIds = savedIdsRaw ? JSON.parse(savedIdsRaw) : [];
      const hasAny = Array.isArray(savedIds) && savedIds.length > 0;
      const savedFlagRaw = localStorage.getItem('showTranslation');
      const savedFlag = savedFlagRaw ? JSON.parse(savedFlagRaw) : hasAny;
      return hasAny && !!savedFlag;
    } catch {
      return false;
    }
  });
  const [selectedTranslations, setSelectedTranslations] = React.useState(() => {
    try {
      const saved = localStorage.getItem('selectedTranslations');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch { }
    return [];
  });
  const [isTranslationSelectorOpen, setIsTranslationSelectorOpen] = React.useState(false);
  const translationSectionRef = React.useRef(null);

  const [reciterMenuOpen, setReciterMenuOpen] = React.useState(false);
  const reciterWrapRef = React.useRef(null);
  const reciterDdRef = React.useRef(null);
  const [reciterMenuPos, setReciterMenuPos] = React.useState({ right: false, above: false });

  React.useEffect(() => {
    const clamp = (n) => Math.max(1, Math.min(7, n || 3));
    const size = clamp(fontSize);
    const multipliers = { 1: 0.7, 2: 0.85, 3: 1.0, 4: 1.2, 5: 1.4, 6: 1.6, 7: 1.8 };
    const multiplier = multipliers[size] || 1.0;
    const uthmaniBoost = qcfStyle === 'v1' ? 1.15 : 1.0;

    const baseAyahSize = 22;
    const ayahSize = Math.round(baseAyahSize * multiplier * uthmaniBoost);
    document.documentElement.style.setProperty('--quran-ayah-font-size', `${ayahSize}px`);

  }, [fontSize, qcfStyle]);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  React.useEffect(() => {
    const onDoc = (e) => { if (!reciterWrapRef.current) return; if (!reciterWrapRef.current.contains(e.target)) setReciterMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  React.useLayoutEffect(() => {
    if (!reciterMenuOpen) return;
    const wrap = reciterWrapRef.current; const dd = reciterDdRef.current; if (!wrap || !dd) return;
    let useRight = false; let useAbove = false;
    const wr = wrap.getBoundingClientRect(); const dr = dd.getBoundingClientRect();
    if (wr.left + dr.width > window.innerWidth - 8) useRight = true;
    if (wr.bottom + 8 + dr.height > window.innerHeight - 8) useAbove = true;
    setReciterMenuPos({ right: useRight, above: useAbove });
  }, [reciterMenuOpen]);

  React.useEffect(() => {
    if (audio?.reciter && audio.reciter !== reciter) setReciter(audio.reciter);
  }, [audio?.reciter]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onScrollTo = () => {
      try {
        translationSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch { }
    };
    window.addEventListener('scrollToTranslationSettings', onScrollTo);
    return () => window.removeEventListener('scrollToTranslationSettings', onScrollTo);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    let ok = true;
    (async () => {
      try {
        const { surah } = await prefetchSurah(1);
        if (!ok) return;
        const wi = surah?.qcf?.words?.find(w => w.number === 1) || null;
        const ay = surah?.alquran_translations?.ayahs?.find(a => a.numberInSurah === 1) || null;
        setPreviewData({
          words: wi?.words || [],
          end_v1: wi?.end_v1 || '',
          end_v4: wi?.end_v4 || '',
          indopakWords: ay?.words_indopak || {},
          indopakEnd: ay?.verse_number_glyph_indopak || ''
        });
      } catch { }
    })();
    return () => { ok = false; };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const sync = () => {
      const s = localStorage.getItem('qcfStyle') || 'v1';
      setQcfStyle(s);
    };
    sync();
    const onChange = (e) => {
      const s = (e && e.detail && e.detail.style) || localStorage.getItem('qcfStyle') || 'v1';
      setQcfStyle(s);
    };
    window.addEventListener('qcfStyleChanged', onChange);
    return () => window.removeEventListener('qcfStyleChanged', onChange);
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      loadSettingsFromServer();
    }
  }, [isOpen]);

  async function loadSettingsFromServer() {
    try {
      const settings = await settingsAPI.syncFromServer();

      if (settings.theme) setTheme(settings.theme);
      const localQcf = localStorage.getItem('qcfStyle');
      if (!localQcf && settings.fontStyle) {
        setQcfStyle(settings.fontStyle);
        localStorage.setItem('qcfStyle', settings.fontStyle);
      }

    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }

  async function saveSettingsToServer() {
    try {
      await settingsAPI.syncToServer();

    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }

  const applyTheme = (t) => {
    setTheme(t);
    localStorage.setItem('theme', t);
    document.documentElement.setAttribute('data-theme', t);
    saveSettingsToServer();
  };

  const applyReciter = (id) => {
    setReciter(id);
    try { localStorage.setItem('selectedReciter', id); } catch { }
    try { audio?.changeReciter?.(id); } catch { }
    saveSettingsToServer();
  };

  const applyQcfStyle = (style) => {
    setQcfStyle(style);
    localStorage.setItem('qcfStyle', style);
    saveSettingsToServer();
    try { window.dispatchEvent(new CustomEvent('qcfStyleChanged', { detail: { style } })); } catch { }
  };

  const changeFontSize = (delta) => {
    const current = Number.isFinite(fontSize) ? fontSize : 3;
    const newSize = Math.max(1, Math.min(7, current + delta));
    setFontSize(newSize);
    localStorage.setItem('fontSize', String(newSize));
    saveSettingsToServer();
    try { window.dispatchEvent(new Event('storage')); } catch { }
  };

  const toggleWordByWord = (field) => {
    const newSettings = { ...wordByWord, [field]: !wordByWord[field] };
    setWordByWord(newSettings);
    localStorage.setItem('wordByWord', JSON.stringify(newSettings));
    saveSettingsToServer();
    try { window.dispatchEvent(new Event('storage')); } catch { }
  };

  const changeWordByWordDisplay = (mode) => {
    setWordByWordDisplay(mode);
    localStorage.setItem('wordByWordDisplay', mode);
    saveSettingsToServer();
    try { window.dispatchEvent(new Event('storage')); } catch { }
  };

  const changeWordByWordLang = (lang) => {
    setWordByWordLang(lang);
    try { localStorage.setItem('wordByWordLang', lang); } catch { }
    saveSettingsToServer();
    try { window.dispatchEvent(new Event('storage')); } catch { }
  };

  const changeTranslationFontSize = (delta) => {
    const current = Number.isFinite(translationFontSize) ? translationFontSize : 3;
    const newSize = Math.max(1, Math.min(7, current + delta));
    setTranslationFontSize(newSize);
    localStorage.setItem('translationFontSize', String(newSize));
    saveSettingsToServer();
  };

  const toggleShowTranslation = () => {
    if (showTranslation) {
      setShowTranslation(false);
      try {
        localStorage.setItem('showTranslation', 'false');
        saveSettingsToServer();
        window.dispatchEvent(new Event('storage'));
      } catch { }
      return;
    }

    if (Array.isArray(selectedTranslations) && selectedTranslations.length > 0) {
      setShowTranslation(true);
      try {
        localStorage.setItem('showTranslation', 'true');
        saveSettingsToServer();
        window.dispatchEvent(new Event('storage'));
      } catch { }
      return;
    }

    setIsTranslationSelectorOpen(true);
  };

  const handleTranslationSelect = (ids) => {
    const next = Array.isArray(ids) ? ids : [];
    setSelectedTranslations(next);
    try {
      localStorage.setItem('selectedTranslations', JSON.stringify(next));
    } catch { }

    const hasAny = next.length > 0;
    setShowTranslation(hasAny);
    try {
      localStorage.setItem('showTranslation', JSON.stringify(hasAny));
      saveSettingsToServer();
      window.dispatchEvent(new Event('storage'));
    } catch { }
  };

  const resetSettings = () => {
    if (window.confirm('Вы уверены, что хотите сбросить все настройки?')) {
      localStorage.removeItem('theme');
      localStorage.removeItem('qcfStyle');
      localStorage.removeItem('fontSize');
      localStorage.removeItem('uiLang');

      setTheme('dark');
      setQcfStyle('v1');
      setFontSize(3);
      setUiLang('ru');

      document.documentElement.setAttribute('data-theme', 'dark');
      saveSettingsToServer();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-modal__header">
          <h2 className="settings-modal__title">{t('settings')}</h2>
          <button className="settings-modal__close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="settings-modal__content">
          <div className="settings-section">
            <h3 className="settings-section__title">{t('reciter')}</h3>
            <div className="stack gap-8" ref={reciterWrapRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setReciterMenuOpen(o => !o)}
                className="settings-translation-btn"
                style={{ justifyContent: 'space-between' }}
                title={RECITERS[reciter]?.name || reciter}
              >
                <span className="translation-label">{t('current_reciter')}</span>
                <div className="translation-value">
                  <span>{RECITERS[reciter]?.name || reciter}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </div>
              </button>
              {reciterMenuOpen && (
                <div
                  ref={reciterDdRef}
                  className="audio-player__speed-dropdown"
                  style={{
                    position: 'absolute',
                    top: reciterMenuPos.above ? 'auto' : '52px',
                    bottom: reciterMenuPos.above ? '52px' : 'auto',
                    right: reciterMenuPos.right ? 0 : 'auto',
                    left: reciterMenuPos.right ? 'auto' : 0,
                    maxHeight: 360,
                    overflowY: 'auto',
                    zIndex: 30
                  }}
                >
                  {(() => {
                    const withHighlight = recitersList.filter(r => RECITERS[r.id]?.wordHighlight !== false);
                    const withoutHighlight = recitersList.filter(r => RECITERS[r.id]?.wordHighlight === false);

                    return (
                      <>
                        {withHighlight.length > 0 && (
                          <>
                            <div className="audio-player__dropdown-section">С подсветкой слов</div>
                            {withHighlight.map(r => (
                              <button
                                key={r.id}
                                className={`audio-player__dropdown-item ${reciter === r.id ? 'active' : ''}`}
                                onClick={() => { applyReciter(r.id); setReciterMenuOpen(false); }}
                                title={r.name}
                              >
                                {reciter === r.id && (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                )}
                                <span className="reciter-name">{r.name}</span>
                              </button>
                            ))}
                          </>
                        )}

                        {withoutHighlight.length > 0 && (
                          <>
                            <div className="audio-player__dropdown-section audio-player__dropdown-section--no-highlight">Без подсветки слов</div>
                            {withoutHighlight.map(r => (
                              <button
                                key={r.id}
                                className={`audio-player__dropdown-item ${reciter === r.id ? 'active' : ''}`}
                                onClick={() => { applyReciter(r.id); setReciterMenuOpen(false); }}
                                title={r.name}
                              >
                                {reciter === r.id && (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                )}
                                <span className="reciter-name">{r.name}</span>
                              </button>
                            ))}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          <div className="settings-divider"></div>

          <div className="settings-section">
            <h3 className="settings-section__title">{t('quran_font_style_title')}</h3>

            <div className="settings-tabs">
              {[
                { id: 'v1', label: t('font_style_uthmani') },
                { id: 'indopak', label: t('font_style_indopak') },
                { id: 'v4', label: t('font_style_tajweed') }
              ].map(opt => (
                <button
                  key={opt.id}
                  className={`settings-tab ${qcfStyle === opt.id ? 'active' : ''}`}
                  onClick={() => applyQcfStyle(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="settings-row">
              <span className="settings-label">{t('font_size')}</span>
              <div className="settings-controls">
                <button
                  className="settings-control-btn"
                  onClick={() => changeFontSize(-1)}
                  disabled={fontSize <= 1}
                >
                  −
                </button>
                <span className="settings-value">{fontSize}</span>
                <button
                  className="settings-control-btn"
                  onClick={() => changeFontSize(1)}
                  disabled={fontSize >= 7}
                >
                  +
                </button>
              </div>
            </div>

            <div className="settings-preview">
              {qcfStyle === 'indopak' ? (
                <span className={'quran-line'} style={{ fontFamily: ensureIndopakFont(), fontSize: 'var(--quran-ayah-font-size)', lineHeight: 1.8, display: 'block', direction: 'rtl', textAlign: 'right' }}>
                  {(() => {
                    const w = previewData?.indopakWords || {};
                    const keys = Object.keys(w).sort((a, b) => Number(a) - Number(b));
                    return keys.map((k, i) => (
                      <span key={i} style={{ padding: '0 3px', display: 'inline-block' }}>{w[k]}</span>
                    ));
                  })()}
                  {previewData?.indopakEnd && (
                    <span style={{ padding: '0 3px', display: 'inline-block' }}>{previewData.indopakEnd}</span>
                  )}
                </span>
              ) : (
                <span className={qcfStyle === 'v4' ? 'qcf-v4 quran-line' : 'quran-line'} style={{ fontSize: 'var(--quran-ayah-font-size)', lineHeight: 2, display: 'block', direction: 'rtl', textAlign: 'right' }}>
                  {(previewData?.words || []).map((w, i) => {
                    const useV4 = (qcfStyle === 'v4' && !!w.code_v4);
                    const glyph = useV4 ? (w.code_v4 || w.code_v1 || '') : (w.code_v1 || '');
                    const fam = ensureQcfFont(w.page, useV4 ? 'v4' : 'v1');
                    return <span key={i} style={{ fontFamily: fam, padding: '0 3px', display: 'inline-block' }}>{glyph}</span>;
                  })}
                  {(() => {
                    const endGlyph = qcfStyle === 'v4' ? (previewData?.end_v4 || previewData?.end_v1 || '') : (previewData?.end_v1 || '');
                    if (!endGlyph) return null;
                    const lastPage = (() => {
                      const arr = previewData?.words || [];
                      if (!arr.length) return null;
                      const lw = arr[arr.length - 1];
                      return lw?.page || arr[0]?.page || null;
                    })();
                    const famEnd = lastPage ? ensureQcfFont(lastPage, qcfStyle === 'v4' ? 'v4' : 'v1') : undefined;
                    return <span style={{ fontFamily: famEnd, padding: '0 3px', display: 'inline-block' }}>{endGlyph}</span>;
                  })()}
                </span>
              )}
            </div>
          </div>

          <div className="settings-divider"></div>

          <div className="settings-section">
            <h3 className="settings-section__title">{t('wbw_title')}</h3>

            <div className="settings-checkbox-group">
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  checked={wordByWord.translation}
                  onChange={() => toggleWordByWord('translation')}
                />
                <span>{t('wbw_translation')}</span>
              </label>
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  checked={wordByWord.transliteration}
                  onChange={() => toggleWordByWord('transliteration')}
                />
                <span>{t('transliteration')}</span>
              </label>
            </div>

            <div className="settings-row" style={{ marginTop: '12px' }}>
              <span className="settings-label">{t('wbw_translation_lang')}</span>
              <div className="settings-display-buttons">
                <button
                  className={`settings-display-btn ${wordByWordLang === 'ru' ? 'active' : ''}`}
                  onClick={() => changeWordByWordLang('ru')}
                >
                  {t('wbw_translation_lang_ru')}
                </button>
                <button
                  className={`settings-display-btn ${wordByWordLang === 'en' ? 'active' : ''}`}
                  onClick={() => changeWordByWordLang('en')}
                >
                  {t('wbw_translation_lang_en')}
                </button>
              </div>
            </div>

            <div className="settings-row" style={{ marginTop: '16px' }}>
              <span className="settings-label">{t('display_title')}</span>
              <div className="settings-display-buttons">
                <button
                  className={`settings-display-btn ${wordByWordDisplay === 'inline' ? 'active' : ''}`}
                  onClick={() => changeWordByWordDisplay('inline')}
                >
                  {t('under_word_value')}
                </button>
                <button
                  className={`settings-display-btn ${wordByWordDisplay === 'tooltip' ? 'active' : ''}`}
                  onClick={() => changeWordByWordDisplay('tooltip')}
                >
                  {t('on_click_value')}
                </button>
              </div>
            </div>
          </div>

          <div className="settings-divider"></div>

          <div className="settings-section" ref={translationSectionRef}>
            <h3 className="settings-section__title">{t('translation_section')}</h3>

            <div className="settings-row">
              <span className="settings-label">{t('show_translation_label')}</span>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={showTranslation}
                  onChange={toggleShowTranslation}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            {showTranslation && (
              <>
                <button
                  className="settings-translation-btn"
                  onClick={() => setIsTranslationSelectorOpen(true)}
                >
                  <span className="translation-label">{t('selected_translations')}</span>
                  <div className="translation-value">
                    <span>
                      {selectedTranslations.length === 0 ? t('not_selected') :
                        selectedTranslations.length === 1 ?
                          getTranslationById(selectedTranslations[0])?.nativeName || t('selected_n', { n: 1 }) :
                          t('selected_n', { n: selectedTranslations.length })
                      }
                    </span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </button>

                {selectedTranslations.length > 0 && (
                  <div className="settings-selected-translations">
                    {selectedTranslations.map((id) => {
                      const translation = getTranslationById(id);
                      if (!translation) return null;
                      return (
                        <div key={id} className="selected-translation-item">
                          <span className="translation-native">{translation.nativeName}</span>
                          <span className="translation-author">{translation.author}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="settings-row">
                  <span className="settings-label">{t('font_size')}</span>
                  <div className="settings-controls">
                    <button
                      className="settings-control-btn"
                      onClick={() => changeTranslationFontSize(-1)}
                      disabled={translationFontSize <= 1}
                    >
                      −
                    </button>
                    <span className="settings-value">{translationFontSize}</span>
                    <button
                      className="settings-control-btn"
                      onClick={() => changeTranslationFontSize(1)}
                      disabled={translationFontSize >= 7}
                    >
                      +
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="settings-divider"></div>

          <div className="settings-section">
            <h3 className="settings-section__title">{t('theme_title')}</h3>

            <div className="settings-theme-grid">
              {themes.map((th) => (
                <button
                  key={th.id}
                  className={`settings-theme-card ${theme === th.id ? 'active' : ''}`}
                  onClick={() => applyTheme(th.id)}
                >
                  <div className="theme-icon">
                    {th.id === 'light' ? '☀️' : '🌙'}
                  </div>
                  <span>{t(th.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="settings-divider"></div>

          <div className="settings-section">
            <button className="settings-reset-btn" onClick={resetSettings}>
              {t('reset_settings')}
            </button>
          </div>
        </div>
      </div>

      <TranslationSelector
        isOpen={isTranslationSelectorOpen}
        onClose={() => setIsTranslationSelectorOpen(false)}
        selectedIds={selectedTranslations}
        onSelect={handleTranslationSelect}
      />
    </div>
  );
}
